import cx from 'classnames';
import _ from 'lodash';
import React, { useCallback, useEffect, useMemo, useRef, useState, } from 'react';
import ReactDOM from 'react-dom';
import { usePopper } from 'react-popper';
import { Key } from 'w3c-keys';
import { cloneChildren } from '../_utils';
import styles from './popover.module.scss';
export const PopoverToggle = ({ content, children, align, showAlignArrow = false, appendToBody = true, enableScroll = false, forceClose, className = '', toggleOnEnter = false, applyTabIndex = true, }) => {
    const [toggle, setToggle] = useState(false);
    const [isFocused, setisFocused] = useState(false);
    const referenceRef = useRef(null);
    const popperRef = useRef(null);
    const arrowRef = useRef(null);
    /** styles is been renamed to style to keep consistent with css styles */
    const { styles: style, attributes } = usePopper(referenceRef.current, popperRef.current, {
        modifiers: [
            { name: 'arrow', options: { element: arrowRef.current } },
            {
                name: 'offset',
                enabled: true,
                options: {
                    offset: [0, 4],
                },
            },
            {
                name: 'preventOverflow',
                options: {
                    mainAxis: true,
                    altBoundary: true,
                },
            },
        ],
        placement: align,
    });
    const onEscapeHandler = (event) => {
        if (event.key === Key.Escape) {
            setToggle(false);
            setisFocused(false);
        }
    };
    useEffect(() => {
        document.addEventListener('keydown', onEscapeHandler);
        return () => {
            document.removeEventListener('keydown', onEscapeHandler);
        };
    }, []);
    /**
     * Hide popper if we click anywhere OUTSIDE
     * the popper or the target node
     */
    const handleDocumentClick = (event) => {
        const isInsideTarget = referenceRef &&
            referenceRef.current &&
            referenceRef.current.contains(event.target);
        const isInsidePopper = popperRef &&
            popperRef.current &&
            popperRef.current.contains(event.target);
        if (isInsidePopper || isInsideTarget) {
            return;
        }
        setToggle(false);
        setisFocused(false);
    };
    /**
     * Hide the popper if a keyboard event occurs when focus is OUTSIDE
     * the popper or the target node
     */
    const handleDocumentKeyPress = () => {
        var _a, _b;
        if (document.activeElement &&
            !((_a = referenceRef.current) === null || _a === void 0 ? void 0 : _a.contains(document.activeElement)) &&
            !((_b = popperRef.current) === null || _b === void 0 ? void 0 : _b.contains(document.activeElement))) {
            setToggle(false);
            setisFocused(false);
        }
    };
    const handleToggle = useCallback(() => {
        setToggle(!toggle);
        window.dispatchEvent(new Event('resize'));
    }, [toggle]);
    useEffect(() => {
        document.addEventListener('mousedown', handleDocumentClick);
        document.addEventListener('keydown', handleDocumentKeyPress);
        return () => {
            document.removeEventListener('mousedown', handleDocumentClick);
            document.removeEventListener('keydown', handleDocumentKeyPress);
        };
    }, []);
    useEffect(() => {
        setToggle(false);
        setisFocused(false);
    }, [forceClose]);
    /** This condition is to check if the resize of window and update the alignment of arrow */
    const updatedArrowAlignment = attributes.popper && attributes.popper['data-popper-placement']
        ? attributes.popper['data-popper-placement']
        : align;
    const positioncss = cx(styles.positionable, { [styles.enableScroll]: enableScroll }, { [styles[updatedArrowAlignment]]: showAlignArrow }, { [className]: true }, toggle ? styles.in : styles.out);
    const renderPopoverContent = () => (React.createElement("div", { ref: popperRef, className: positioncss, style: { ...style.popper }, ...attributes.popper, tabIndex: 0, role: "button" },
        content,
        React.createElement("div", { ref: arrowRef, style: style.arrow, className: styles.arrow })));
    const handleKeyDown = useMemo(() => {
        return toggleOnEnter
            ? (event) => {
                if (event.key === 'Enter' && !isFocused) {
                    event.stopPropagation();
                    handleToggle();
                }
            }
            : _.noop;
    }, [toggleOnEnter, isFocused, handleToggle]);
    return (React.createElement("div", { onKeyDown: handleKeyDown, tabIndex: applyTabIndex ? 0 : undefined, role: "button" },
        cloneChildren(children, {
            ref: referenceRef,
            onClick: (event) => {
                event.stopPropagation();
                handleToggle();
            },
        }),
        toggle && appendToBody
            ? ReactDOM.createPortal(renderPopoverContent(), document.body)
            : renderPopoverContent()));
};
//# sourceMappingURL=popover-toggle.js.map